/**
 * RitualCelebration - Celebration animations for ritual completions
 * Shows confetti, streak milestones, and completion celebrations
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Star, Trophy, Sparkles, Heart, Crown, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RitualCelebrationProps {
  show: boolean;
  type: 'completion' | 'streak_milestone' | 'both_complete';
  streakCount?: number;
  routineType?: 'morning' | 'evening';
  onComplete?: () => void;
}

const STREAK_MILESTONES = [3, 7, 14, 21, 30, 60, 90, 100, 365];

const getMilestoneMessage = (streak: number) => {
  if (streak === 3) return "3 Days Strong!";
  if (streak === 7) return "One Week Wonder!";
  if (streak === 14) return "Two Week Champion!";
  if (streak === 21) return "Habit Formed!";
  if (streak === 30) return "Monthly Master!";
  if (streak === 60) return "Two Month Titan!";
  if (streak === 90) return "Quarter Year Hero!";
  if (streak === 100) return "Century Club!";
  if (streak === 365) return "Year of Dedication!";
  return `${streak} Day Streak!`;
};

const getMilestoneIcon = (streak: number) => {
  if (streak >= 100) return Crown;
  if (streak >= 30) return Trophy;
  if (streak >= 14) return Star;
  if (streak >= 7) return Zap;
  return Flame;
};

// Confetti particle component
const Confetti: React.FC<{ delay: number; color: string }> = ({ delay, color }) => {
  const randomX = Math.random() * 100;
  const randomRotation = Math.random() * 360;
  
  return (
    <motion.div
      initial={{ 
        y: -20, 
        x: `${randomX}vw`, 
        rotate: 0, 
        opacity: 1,
        scale: 0 
      }}
      animate={{ 
        y: '100vh', 
        rotate: randomRotation + 720, 
        opacity: [1, 1, 0],
        scale: [0, 1, 1]
      }}
      transition={{ 
        duration: 3 + Math.random() * 2,
        delay: delay,
        ease: "easeOut"
      }}
      className="absolute w-3 h-3 rounded-sm"
      style={{ 
        backgroundColor: color,
        left: 0,
        top: 0
      }}
    />
  );
};

// Sparkle burst component
const SparkleBurst: React.FC<{ count?: number }> = ({ count = 8 }) => {
  return (
    <div className="absolute inset-0 pointer-events-none">
      {Array.from({ length: count }).map((_, i) => {
        const angle = (i / count) * 360;
        return (
          <motion.div
            key={i}
            initial={{ scale: 0, opacity: 1 }}
            animate={{ 
              scale: [0, 1.5, 0],
              opacity: [1, 1, 0],
              x: [0, Math.cos(angle * Math.PI / 180) * 80],
              y: [0, Math.sin(angle * Math.PI / 180) * 80]
            }}
            transition={{ duration: 0.8, delay: i * 0.05 }}
            className="absolute top-1/2 left-1/2"
          >
            <Sparkles className="w-4 h-4 text-secondary" />
          </motion.div>
        );
      })}
    </div>
  );
};

export const RitualCelebration: React.FC<RitualCelebrationProps> = ({
  show,
  type,
  streakCount = 0,
  routineType,
  onComplete
}) => {
  const [showContent, setShowContent] = useState(false);
  
  const isMilestone = STREAK_MILESTONES.includes(streakCount);
  const MilestoneIcon = getMilestoneIcon(streakCount);
  
  const confettiColors = [
    'hsl(var(--primary))',
    'hsl(var(--secondary))',
    'hsl(var(--accent))',
    'hsl(var(--soft-accent))',
  ];

  useEffect(() => {
    if (show) {
      setShowContent(true);
      const timer = setTimeout(() => {
        setShowContent(false);
        onComplete?.();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  return (
    <AnimatePresence>
      {showContent && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
        >
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-background"
          />

          {/* Confetti */}
          <div className="absolute inset-0 overflow-hidden">
            {Array.from({ length: 50 }).map((_, i) => (
              <Confetti
                key={i}
                delay={i * 0.05}
                color={confettiColors[i % confettiColors.length]}
              />
            ))}
          </div>

          {/* Main celebration content */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", duration: 0.6 }}
            className="relative z-10"
          >
            <div className="relative">
              <SparkleBurst count={12} />
              
              <motion.div
                className="flex flex-col items-center gap-4 p-8"
                initial={{ y: 20 }}
                animate={{ y: 0 }}
              >
                {/* Icon */}
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, -10, 10, 0]
                  }}
                  transition={{ 
                    duration: 0.5,
                    repeat: 2,
                    repeatDelay: 0.5
                  }}
                  className={cn(
                    "w-20 h-20 rounded-full flex items-center justify-center",
                    type === 'both_complete' 
                      ? "bg-gradient-to-br from-secondary/30 to-primary/30"
                      : routineType === 'morning'
                        ? "bg-secondary/20"
                        : "bg-primary/20"
                  )}
                >
                  {isMilestone ? (
                    <MilestoneIcon className="w-10 h-10 text-secondary" />
                  ) : type === 'both_complete' ? (
                    <Heart className="w-10 h-10 text-primary" />
                  ) : (
                    <Sparkles className="w-10 h-10 text-primary" />
                  )}
                </motion.div>

                {/* Message */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-center"
                >
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    {type === 'both_complete' 
                      ? "Perfect Day!" 
                      : isMilestone 
                        ? getMilestoneMessage(streakCount)
                        : `${routineType === 'morning' ? 'Morning' : 'Evening'} Complete!`}
                  </h2>
                  <p className="text-muted-foreground">
                    {type === 'both_complete' 
                      ? "Both habits complete - you showed up fully today"
                      : isMilestone
                        ? "You're building an incredible foundation"
                        : streakCount > 0 
                          ? `${streakCount} day${streakCount !== 1 ? 's' : ''} strong`
                          : "Great start to your healthy habits journey!"}
                  </p>
                </motion.div>

                {/* Streak display */}
                {streakCount > 0 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 }}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/20"
                  >
                    <Flame className="w-5 h-5 text-secondary" />
                    <span className="font-bold text-secondary">{streakCount}</span>
                    <span className="text-sm text-muted-foreground">day streak</span>
                  </motion.div>
                )}
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const isMilestoneStreak = (streak: number) => STREAK_MILESTONES.includes(streak);
